import { SidebarMenu } from '@ardoq/sidebar-menu';
import { GhostButton } from '@ardoq/button';
import { Icon, IconName } from '@ardoq/icons';
import {
  AddFilterCommands,
  ChangeConditionCommands,
  EditFilterCommands,
} from './viewpointBuilderFiltersCommands';
import { TraversalBuilderViewState } from '../types';
import { ViewpointBuilderFilters } from './ViewpointBuilderFilters';
import { DoqContainer } from '../components/atoms';
import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { SidebarHeader } from 'viewpointBuilder/components/atoms';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { FlexBox } from '@ardoq/layout';

type AddFiltersSidebarProps = {
  commands: AddFilterCommands & ChangeConditionCommands & EditFilterCommands;
  state: TraversalBuilderViewState;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export const AddFiltersSidebar = ({
  state,
  commands,
  locale,
  i18nLocale,
}: AddFiltersSidebarProps) => {
  const selectedId = state.selectedGraphNodeId ?? state.selectedGraphEdgeId;

  if (!selectedId) {
    return (
      <FlexBox
        height="full"
        width="full"
        gap="medium"
        backgroundColor="surfaceDefault"
        justify="center"
      >
        <DoqContainer>
          <ResponsiveDoqWithSpeechBubble
            doqType={DoqType.SIMPLE}
            title={t(
              i18nLocale,
              'viewpointBuilder:editTraversal.noSelection.title',
              'Start with a component type'
            )}
            message={t(
              i18nLocale,
              'viewpointBuilder:addFilters.sidebar.noSelection.message',
              'Select a component or reference type on the canvas on the left to configure filters'
            )}
          />
        </DoqContainer>
      </FlexBox>
    );
  }

  const isEdgeSelected = Boolean(state.selectedGraphEdgeId);
  const filterOfSelectedNode = state.filters[selectedId];
  const isAnyFilterApplied = filterOfSelectedNode?.filters.length > 0;

  return (
    <SidebarMenu
      isPageBodyMenu
      isResizable
      isRightContent
      headerBody={
        <SidebarHeader
          type={isEdgeSelected ? 'reference' : 'component'}
          // @ts-expect-error passing potentially undefined here, even though it's not permitted
          selectedType={
            isEdgeSelected
              ? state.tripleOptions.selectedEdge?.metaDataComponent
              : state.tripleOptions.selectedNode.metaDataComponent
          }
          headerText={t(
            i18nLocale,
            'viewpointBuilder:addFilters.sidebar.header',
            'Add filters to'
          )}
          iconName={IconName.FILTER_LIST}
        />
      }
    >
      {isAnyFilterApplied ? (
        <ViewpointBuilderFilters
          availableFilterGroupedOptions={
            state.filtersDefinitions.componentFilterTypeOptions
          }
          asyncLoaders={state.asyncLoaders}
          commands={commands}
          filtersWithCondition={filterOfSelectedNode}
          selectedGraphNodeId={selectedId}
          locale={locale}
          i18nLocale={i18nLocale}
        />
      ) : (
        <NoFiltersApplied
          commands={commands}
          selectedGraphNodeId={selectedId}
          i18nLocale={i18nLocale}
        />
      )}
    </SidebarMenu>
  );
};

type NoFiltersAppliedProps = {
  commands: AddFilterCommands;
  selectedGraphNodeId: string;
  i18nLocale: I18nLocale;
};

const NoFiltersApplied = ({
  commands,
  selectedGraphNodeId,
  i18nLocale,
}: NoFiltersAppliedProps) => {
  return (
    <FlexBox
      height="full"
      width="full"
      gap="medium"
      backgroundColor="surfaceDefault"
      justify="center"
      flex={1}
      flexDirection="column"
      align="center"
    >
      <span>
        {t(
          i18nLocale,
          'viewpointBuilder:addFilters.noFilters.text',
          'No filters added yet'
        )}
      </span>
      <GhostButton
        onClick={() => commands.addDefaultFilter(selectedGraphNodeId)}
      >
        <Icon iconName={IconName.ADD_CIRCLE} />
        {t(
          i18nLocale,
          'viewpointBuilder:addFilters.noFilters.addButton',
          'Add a filter'
        )}
      </GhostButton>
    </FlexBox>
  );
};
